import { Fade, styled } from '@mui/material'
import { ChainInfo } from 'api/v1/chains'
import toastify from 'components/Toastify'
import { ConnectionType } from 'connection/types'
import { memo, useEffect } from 'react'
import {
  useCurrentNetwork,
  useIsErrorChain,
  useSupportChains,
  useUnsupportedChains,
} from 'store/app/hooks'
import { useCurrentConnectorName } from 'store/settings/hooks'
import { useWeb3Connected } from '../../../store/web3/hooks'
import { useSwitchNetwork } from '../../../hooks/web3/useSwitchNetwork'
import { useAtom, useSetAtom } from 'jotai'
import { selectedNetwork, showNetSelectPopup } from './atoms'
import {
  CurrentChainFlag,
  NetworkIcon,
  NetworkOption,
  NetworkPopupList,
} from './Popup.styles'
import { HOT_CHAINS, NEW_CHAINS } from 'constants/chains'
import { MobileHead } from '../MobileHead'
import { menuAtom } from '../Menu/atoms'

const Tag = styled('span')`
  display: inline-flex;
  align-items: center;
  height: 20px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: rgba(167, 239, 12, 1);
  padding: 0 8px;
  border-radius: 26px;
  background: linear-gradient(
    270deg,
    rgba(161, 238, 10, 0.1) 0.09%,
    rgba(211, 238, 19, 0.1) 99.91%
  );
`

export const NetworkSelectPopup = memo(() => {
  const supportChains = useSupportChains()
  const unSupportChains = useUnsupportedChains()
  const isErrorChain = useIsErrorChain()
  const web3Connected = useWeb3Connected()
  const currentConnectorName = useCurrentConnectorName()
  const currentNetwork = useCurrentNetwork()
  const switchNetwork = useSwitchNetwork()
  const [selected, setSelected] = useAtom(selectedNetwork)
  const setMenu = useSetAtom(menuAtom)

  useEffect(() => {
    if (!web3Connected) {
      setSelected(supportChains[0])
    }
    // If the current network is valid and the selected network
    // is not the same as the current network, display the current network
    if (
      web3Connected &&
      currentNetwork?.chainId &&
      currentNetwork?.chainId !== selected?.chainId
    ) {
      setSelected(currentNetwork)
    }
  }, [selected, setSelected, currentNetwork, supportChains, web3Connected])

  useEffect(() => {
    if (isErrorChain) {
      setSelected(undefined)
    }
  }, [setSelected, isErrorChain])

  const onSelectNetwork = (selectedNetwork: ChainInfo) => {
    if (
      web3Connected &&
      currentConnectorName === ConnectionType.WALLET_CONNECT_V2
    ) {
      toastify.error(
        'Could not switch networks from the ZKEX interface. Please change the network in your web3.',
        {
          id: 'failed switch network',
        },
      )
    }
    if (web3Connected) {
      if (
        selectedNetwork.layerOneChainId !== currentNetwork?.layerOneChainId ||
        isErrorChain
      ) {
        switchNetwork(selectedNetwork.layerOneChainId).catch((e) => {})
      }
    } else {
      setSelected(selectedNetwork)
    }
  }

  return (
    <NetworkPopupList>
      <MobileHead onClose={() => setMenu(0)} />
      {supportChains?.map((item) => {
        const isHot = HOT_CHAINS.includes(item.layerOneChainId)
        const isNew = NEW_CHAINS.includes(item.layerOneChainId)
        return (
          <NetworkOption
            key={item.chainId}
            className={selected?.chainId === item.chainId ? 'active' : ''}
            onClick={(e) => {
              onSelectNetwork(item)
              e.stopPropagation()
            }}
          >
            <NetworkIcon src={item.iconUrl} />
            <div>{item.name}</div>
            {selected?.chainId === item.chainId && <CurrentChainFlag />}

            {isHot ? <Tag>Hot</Tag> : isNew ? <Tag>New</Tag> : null}
          </NetworkOption>
        )
      })}
      {unSupportChains?.map((item) => {
        return (
          <NetworkOption key={item.name} $disabled>
            <NetworkIcon src={item.iconUrl} />
            <div>{item.name}</div>
          </NetworkOption>
        )
      })}
    </NetworkPopupList>
  )
})
